import { z } from "zod";
const isoDate = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, "Date must be in YYYY-MM-DD format");
// Whole-unit filing (e.g. NL): the amounts as declared on the return, in whole
// currency units. Only meaningful for jurisdictions whose
// vat_filing_rounding_policy is a whole-unit one — the RPC rejects them
// elsewhere. Must be provided together or not at all.
const filedAmountFields = {
    filed_output_amount: z.number().int().nullable().optional(),
    filed_input_amount: z.number().int().nullable().optional(),
};
const filedAmountsTogether = (v) => (v.filed_output_amount == null) === (v.filed_input_amount == null);
/**
 * Direction of a settlement payment, stored per payment row.
 *
 * `pay` = money leaves the org (a payment to the authority, or a repayment of
 * an over-refund / naheffingsaanslag); `refund` = money comes back. It is NOT
 * derived from the settlement's sign: after an over-refund the org owes money
 * back on a settlement whose `net_due` is negative.
 *
 * Mirrors the CHECK constraint on `vat_settlement_payments.direction`
 * (migration `*_vat_settlement_payment_direction`). KEEP IN SYNC with that
 * CHECK; both sides carry this comment.
 */
export const VAT_PAYMENT_DIRECTIONS = ["pay", "refund"];
export const vatPaymentDirectionSchema = z.enum(VAT_PAYMENT_DIRECTIONS);
/**
 * Kind of settlement row: the period's regular return, or a supplementary
 * filing (NL: suppletie) correcting an already-filed period.
 *
 * Mirrors the CHECK constraint on `vat_settlements.kind` (migration
 * `*_vat_supplementary_filing_and_sweep`). KEEP IN SYNC with that CHECK; both
 * sides carry this comment.
 */
export const VAT_SETTLEMENT_KINDS = ["original", "supplementary"];
export const vatSettlementKindSchema = z.enum(VAT_SETTLEMENT_KINDS);
/**
 * Filing options shared by `/file` and `/file-and-pay`: which kind of return
 * this is, and which earlier settlements' remaining balances are netted into
 * it ("correct it on the next aangifte"). Each carried-forward id becomes an
 * offset pair inside the filing transaction, so the new return's remaining is
 * what the authority will actually pay or collect.
 */
const filingKindFields = {
    kind: vatSettlementKindSchema.optional(),
    carry_forward_settlement_ids: z.array(z.string().uuid()).max(50).optional(),
};
export const fileVatReturnSchema = z
    .object({
    organization_id: z.string().uuid(),
    period_start: isoDate,
    period_end: isoDate,
    filing_date: isoDate,
    tax_authority_business_id: z.string().uuid(),
    filing_reference: z.string().max(255).nullable().optional(),
    notes: z.string().max(2000).nullable().optional(),
    ...filedAmountFields,
    ...filingKindFields,
})
    .strict()
    .refine((v) => v.period_end >= v.period_start, {
    path: ["period_end"],
    message: "Period end must be on or after period start",
})
    .refine(filedAmountsTogether, {
    path: ["filed_input_amount"],
    message: "Provide both filed amounts or neither",
});
/**
 * Record a payment against a settlement.
 *
 * A payment settles EITHER against a bank account (money actually moved) OR
 * against another settlement (`offset_settlement_id` — the authority netted
 * one period against another; no bank leg, two payment rows sharing an
 * `offset_group_id`). Exactly one of the two, mirroring the database's
 * `(bank_account_id IS NULL) = (offset_group_id IS NOT NULL)` CHECK.
 *
 * `direction` is optional for a bank payment (the RPC defaults it from the
 * sign of the remaining balance) and REQUIRED for an offset, where there is no
 * bank movement to read it from and the counterpart takes the opposite side.
 */
export const recordVatPaymentSchema = z
    .object({
    payment_amount: z.number().positive(),
    payment_date: isoDate,
    bank_account_id: z.string().uuid().optional(),
    payment_reference: z.string().max(255).nullable().optional(),
    notes: z.string().max(2000).nullable().optional(),
    direction: vatPaymentDirectionSchema.optional(),
    offset_settlement_id: z.string().uuid().optional(),
})
    .strict()
    .superRefine((v, ctx) => {
    const hasBank = v.bank_account_id != null;
    const hasOffset = v.offset_settlement_id != null;
    if (hasBank && hasOffset) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["offset_settlement_id"],
            message: "Provide a bank account or an offset settlement, not both",
        });
    }
    else if (!hasBank && !hasOffset) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["bank_account_id"],
            message: "A bank account or an offset settlement is required",
        });
    }
    if (hasOffset && v.direction == null) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["direction"],
            message: "Direction is required for an offset",
        });
    }
});
export const fileAndPayVatReturnSchema = z
    .object({
    organization_id: z.string().uuid(),
    period_start: isoDate,
    period_end: isoDate,
    filing_date: isoDate,
    tax_authority_business_id: z.string().uuid(),
    filing_reference: z.string().max(255).nullable().optional(),
    payment_amount: z.number().positive(),
    payment_date: isoDate,
    bank_account_id: z.string().uuid(),
    payment_reference: z.string().max(255).nullable().optional(),
    notes: z.string().max(2000).nullable().optional(),
    ...filedAmountFields,
    ...filingKindFields,
})
    .strict()
    .refine((v) => v.period_end >= v.period_start, {
    path: ["period_end"],
    message: "Period end must be on or after period start",
})
    .refine(filedAmountsTogether, {
    path: ["filed_input_amount"],
    message: "Provide both filed amounts or neither",
});
export const voidVatPaymentSchema = z
    .object({
    void_reason: z.string().max(500).nullable().optional(),
})
    .strict();
export const voidVatReturnFilingSchema = z
    .object({
    void_reason: z.string().max(500).nullable().optional(),
})
    .strict();
/**
 * `GET /api/v1/vat-settlements/report` query. Mirrors `get_tax_report`'s
 * modes: a date window (both dates), optionally unsettled-only; or a single
 * settlement's tagged entries (`vat_settlement_id`), for which the window is
 * optional. Query strings arrive as strings, so the flag is 'true' | 'false'.
 */
export const taxReportQuerySchema = z
    .object({
    period_start: isoDate.optional(),
    period_end: isoDate.optional(),
    unsettled_only: z.enum(['true', 'false']).optional(),
    vat_settlement_id: z.string().uuid().optional(),
})
    .strict()
    .refine((v) => (!!v.period_start && !!v.period_end) || !!v.vat_settlement_id, { message: 'Provide period_start and period_end, or vat_settlement_id' })
    .refine((v) => !v.period_end || !v.period_start || v.period_end >= v.period_start, {
    path: ['period_end'],
    message: 'Period end must be on or after period start',
});
export const listVatSettlementsQuerySchema = z
    .object({
    organization_id: z.string().uuid(),
    from: isoDate.optional(),
    to: isoDate.optional(),
})
    .strict();
//# sourceMappingURL=vatSettlement.js.map